import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  LinearProgress,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from 'axios';
import WelcomeFlow from '../components/WelcomeFlow';
import BankStatementUploader from '../components/BankStatementUploader';
import {
  FundingPipeline,
  NextStepCard,
  FundingSnapshotCard,
  ProgramsPreview,
  CrossSellBanner,
  ActionChecklist,
  DSCRSummary,
  BankStatementSummary,
} from '../components/dashboard';
import {
  buildStages,
  countDocumentsByType,
  countUploadedDocuments,
  getUnlockState,
} from '../components/dashboard/guidedSteps';
import { countUnlockedPrograms, getPrograms } from '../components/dashboard/ProgramsPreview';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { loginWithSingleUseToken, token: authToken, user } = useAuth();
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSubmittedDialog, setShowSubmittedDialog] = useState(false);
  const [checkingSubmission, setCheckingSubmission] = useState(false);
  const [submissionType, setSubmissionType] = useState(null);
  const [verifyingToken, setVerifyingToken] = useState(false);
  const [docList, setDocList] = useState([]);
  const bankUploaderRef = useRef(null);

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboard.goodMorning');
    if (hour < 17) return t('dashboard.goodAfternoon');
    return t('dashboard.goodEvening');
  };

  // Calculate current stage based on user status
  const calculateStage = (status, docsReady) => {
    if (!status) return 1;
    
    // For now, we'll determine stage based on what we know
    // Stage 1: Applied/PFS completed (first step done)
    // Stage 2: Documents in progress
    // Stage 3: Under review
    // Stage 4: Approved
    // Stage 5: Closing
    // Stage 6: Funded
    
    if (status.applicationStatus === 'funded' || status.pfsStatus === 'funded') return 6;
    if (status.applicationStatus === 'closing' || status.pfsStatus === 'closing') return 5;
    if (status.applicationStatus === 'approved' || status.pfsStatus === 'approved') return 4;
    if (status.applicationStatus === 'review' || status.pfsStatus === 'review') return 3;
    if (docsReady) return 2;
    return 1;
  };

  // Handle authToken for auto-login when returning from external apps
  useEffect(() => {
    const authTokenParam = searchParams.get('authToken');
    
    if (authTokenParam && !authToken) {
      setVerifyingToken(true);
      
      const verifyAndLogin = async () => {
        const result = await loginWithSingleUseToken(authTokenParam);
        
        searchParams.delete('authToken');
        setSearchParams(searchParams, { replace: true });
        
        if (result.success) {
          if (result.needsLogin) {
            toast.info(t('toasts.pleaseLogIn'));
            navigate('/login');
          } else {
            toast.success(t('toasts.welcomeBack'));
          }
        } else if (result.rateLimited) {
          // Handle 429 Too Many Requests from financing-app auto-login
          toast.warning(result.message || t('toasts.tooManyAttempts'));
          // Respect Retry-After: wait and suggest manual login
          setTimeout(() => {
            toast.info(t('welcome.canLoginManually'));
          }, 3000);
          navigate('/login');
        } else {
          toast.error(result.message || t('toasts.sessionExpired'));
          navigate('/login');
        }
        
        setVerifyingToken(false);
      };
      
      verifyAndLogin();
    } else if (authTokenParam && authToken) {
      searchParams.delete('authToken');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, authToken, loginWithSingleUseToken, navigate, setSearchParams]);

  useEffect(() => {
    const submitted = searchParams.get('submitted');
    const pfsSubmitted = searchParams.get('pfs_submitted');
    const source = searchParams.get('source');
    
    if (submitted === 'true' || pfsSubmitted === 'true') {
      // Determine type immediately (not relying on state)
      const isPFS = pfsSubmitted === 'true' || source === 'pfs-copilot';
      const currentSubmissionType = isPFS ? 'pfs' : 'application';
      
      setSubmissionType(currentSubmissionType);
      setShowSubmittedDialog(true);
      setCheckingSubmission(true);
      
      searchParams.delete('submitted');
      searchParams.delete('pfs_submitted');
      searchParams.delete('source');
      setSearchParams(searchParams);
      
      let pollCount = 0;
      const maxPolls = 15;
      const pollInterval = setInterval(async () => {
        pollCount++;
        const status = await fetchUserStatus(true);
        
        // Use the local variable, not state
        const hasExpected = currentSubmissionType === 'pfs' 
          ? status?.hasPFS 
          : status?.hasApplication;
        
        if (hasExpected || pollCount >= maxPolls) {
          clearInterval(pollInterval);
          setCheckingSubmission(false);
          setShowSubmittedDialog(false);
          
          if (hasExpected) {
            const successMsg = currentSubmissionType === 'pfs' 
              ? 'Your SBA Form 413 has been submitted successfully!'
              : 'Your business application has been submitted successfully!';
            toast.success(successMsg);
          } else {
            toast.info('Your submission has been received! It may take a moment to process.');
          }
        }
      }, 2000);
      
      return () => clearInterval(pollInterval);
    } else {
      fetchUserStatus();
    }
  }, []);

  const fetchUserStatus = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return null;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };

      const [statusResponse, docsResponse] = await Promise.all([
        axios.get(`${import.meta.env.VITE_API_URL}/api/requirements/status`, config),
        axios.get(`${import.meta.env.VITE_API_URL}/api/documents`, config).catch((err) => {
          console.error('Error fetching documents:', err);
          return null;
        }),
      ]);

      console.log('User Status Response:', statusResponse.data);
      if (docsResponse) setDocList(docsResponse.data?.data || []);
      
      if (statusResponse.data.success) {
        setUserStatus(statusResponse.data.data);
        return statusResponse.data.data;
      }
      return null;
    } catch (error) {
      console.error('Dashboard Error:', error);
      
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        return null;
      }
      
      if (!silent) {
        setError('fetchError');
        toast.error(error.response?.data?.message || t('toasts.failedToLoadDashboard'));
      }
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ width: '100%' }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
            {t('common.loading')}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          {t('toasts.failedToLoadError')}
        </Alert>
      </Container>
    );
  }

  // Show WelcomeFlow if user has not completed application or PFS
  if (!userStatus?.hasApplication && !userStatus?.hasPFS) {
    return <WelcomeFlow />;
  }

  // Determine which flow(s) the user is on
  const hasApplication = userStatus?.hasApplication;
  const hasPFS = userStatus?.hasPFS;
  const hasBoth = hasApplication && hasPFS;

  // Guided document path drives the pipeline, program unlocks and snapshot.
  const docCounts = countDocumentsByType(docList);
  const guidedStages = buildStages(t, hasApplication, (docType) => docCounts[docType] || 0);
  const activeStageIndex = guidedStages.findIndex((s) => !s.done);
  const currentGuidedStep = activeStageIndex === -1 ? null : {
    number: activeStageIndex + 1,
    total: guidedStages.length,
    title: guidedStages[activeStageIndex].title,
  };
  const unlockState = getUnlockState(guidedStages, hasApplication, hasPFS);
  const docsReady = guidedStages[0].done;
  const scrollToNextSteps = () => document.getElementById('next-steps')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // Calculate stages for each flow
  const fastFundingStage = hasApplication ? calculateStage(userStatus, docsReady) : 0;
  const sbaStage = hasPFS ? calculateStage(userStatus, docsReady) : 0;

  const renderSnapshot = (flow, wide = false) => (
    <FundingSnapshotCard
      type={flow}
      wide={wide}
      amountRequested={userStatus?.applicationData?.loanAmount || 0}
      currentStep={currentGuidedStep}
      documentsUploaded={countUploadedDocuments(docList)}
      programsUnlocked={countUnlockedPrograms(flow, unlockState)}
      totalPrograms={getPrograms(flow).length}
    />
  );

  // Dual Track View (both flows)
  if (hasBoth) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Welcome Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
            {getGreeting()}, {user?.firstName || 'there'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.twoFundingPaths')}
          </Typography>
        </Box>

        {/* Bank statements: the #1 action for every client */}
        <Box sx={{ mb: 3 }}>
          <BankStatementUploader ref={bankUploaderRef} documents={docList} onUploaded={() => fetchUserStatus(true)} />
        </Box>

        {/* Dual Pipelines */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <FundingPipeline type="fastFunding" currentStage={fastFundingStage} compact />
          </Grid>
          <Grid item xs={12} md={6}>
            <FundingPipeline type="sba" currentStage={sbaStage} compact />
          </Grid>
        </Grid>

        {/* Action Checklist - Your Next Steps */}
        <Box sx={{ mb: 3 }}>
          <ActionChecklist
            type="fastFunding"
            hasApplication={hasApplication}
            hasPFS={hasPFS}
            documents={docList}
            onBankStatementsClick={() => bankUploaderRef.current?.open()}
            onDocumentsChanged={() => fetchUserStatus(true)}
          />
        </Box>

        {/* DSCR Summary (shows if user has uploaded tax returns) */}
        <DSCRSummary />

        {/* Bank Statement Summary (shows if user has uploaded bank statements) */}
        <BankStatementSummary />

        {/* Programs Preview */}
        <Box sx={{ mb: 3 }}>
          <ProgramsPreview
            type="fastFunding"
            unlockState={unlockState}
            onUnlockClick={scrollToNextSteps}
          />
        </Box>

        {/* Both applications are in, so show progress instead of a cross-sell */}
        <Box sx={{ mb: 3 }}>
          {renderSnapshot('fastFunding', true)}
        </Box>

        {/* Submission Dialog */}
        <Dialog 
          open={showSubmittedDialog} 
          onClose={() => !checkingSubmission && setShowSubmittedDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
            {checkingSubmission 
              ? (submissionType === 'pfs' ? t('dashboard.processingSbaForm') : t('dashboard.processingApplication'))
              : t('dashboard.submissionReceived')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 3 }}>
              {checkingSubmission ? (
                <>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 3,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress size={40} />
                  </Box>
                  <Typography variant="body1" gutterBottom>
                    {submissionType === 'pfs' 
                      ? t('dashboard.processingSbaFormDesc')
                      : t('dashboard.processingBusinessApp')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.onlyMoment')}
                  </Typography>
                </>
              ) : (
                <>
                  <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom color="success.main">
                    {t('dashboard.submissionComplete')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {submissionType === 'pfs'
                      ? t('dashboard.sbaFormReceived')
                      : t('dashboard.businessAppReceived')}
                  </Typography>
                </>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      </Container>
    );
  }

  // Single Flow View (Fast Funding OR SBA)
  const flowType = hasApplication ? 'fastFunding' : 'sba';
  const currentStage = hasApplication ? fastFundingStage : sbaStage;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Welcome Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
          {getGreeting()}, {user?.firstName || 'there'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {hasApplication ? t('dashboard.fastFundingProgress') : t('dashboard.sbaLoanProgress')}
        </Typography>
      </Box>

      {/* Bank statements: the #1 action for every client */}
      <Box sx={{ mb: 3 }}>
        <BankStatementUploader ref={bankUploaderRef} documents={docList} onUploaded={() => fetchUserStatus(true)} />
      </Box>

      {/* Main Pipeline */}
      <Box sx={{ mb: 3 }}>
        <FundingPipeline type={flowType} currentStage={currentStage} />
      </Box>

      {/* Action Checklist - Your Next Steps */}
      <Box sx={{ mb: 3 }}>
        <ActionChecklist
          type={flowType}
          hasApplication={hasApplication}
          hasPFS={hasPFS}
          documents={docList}
          onBankStatementsClick={() => bankUploaderRef.current?.open()}
          onDocumentsChanged={() => fetchUserStatus(true)}
        />
      </Box>

      {/* DSCR Summary (shows if user has uploaded tax returns) */}
      <DSCRSummary />

      {/* Bank Statement Summary (shows if user has uploaded bank statements) */}
      <BankStatementSummary />

      {/* Programs Preview */}
      <Box sx={{ mb: 3 }}>
        <ProgramsPreview
          type={flowType}
          unlockState={unlockState}
          onUnlockClick={scrollToNextSteps}
        />
      </Box>

      {/* Cross-Sell Banner and Funding Snapshot */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <CrossSellBanner currentType={flowType} />
        </Grid>

        <Grid item xs={12} md={5}>
          {renderSnapshot(flowType)}
        </Grid>
      </Grid>

      {/* Submission Processing Dialog */}
      <Dialog 
        open={showSubmittedDialog} 
        onClose={() => !checkingSubmission && setShowSubmittedDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
          {checkingSubmission 
            ? (submissionType === 'pfs' ? t('dashboard.processingSbaForm') : t('dashboard.processingApplication'))
            : t('dashboard.submissionReceived')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            {checkingSubmission ? (
              <>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 3,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={40} />
                </Box>
                <Typography variant="body1" gutterBottom>
                  {submissionType === 'pfs' 
                    ? t('dashboard.processingSbaFormDesc')
                    : t('dashboard.processingBusinessApp')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('dashboard.onlyMoment')}
                </Typography>
              </>
            ) : (
              <>
                <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom color="success.main">
                  {t('dashboard.submissionComplete')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {submissionType === 'pfs'
                    ? t('dashboard.sbaFormReceived')
                    : t('dashboard.businessAppReceived')}
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Dashboard;
